import { useCallback, useContext, useMemo } from 'react';
import { DealerContext, type DealerOption } from './Context';

export const useDealerContext = () => {
    const context = useContext(DealerContext);

    if (!context) {
        throw new Error('Dealer context is missing');
    }

    return context;
};

// Use Single dealerId
// Should be use in CI
export const useSingleDealerId = () => {
    const { dealers, setDealers, dealerOptions } = useDealerContext();

    // Toggle will make sure that only one dealer is selected
    const toggle = useCallback(
        (newDealerId: string) => {
            const newDealerState = dealers?.map(dealer => {
                if (dealer.id === newDealerId) {
                    return { ...dealer, isSingle: true };
                }

                return { ...dealer, isSingle: false };
            });

            setDealers(newDealerState);
        },
        [dealers, setDealers]
    );

    const dealerId = useMemo(() => {
        const singleDealers = dealers?.filter(dealer => dealer.isSingle);

        // Make sure that dealer is only one
        if (singleDealers.length > 1) {
            throw new Error("There's more than one single dealer");
        }
        if (singleDealers.length === 0) {
            throw new Error("There's no single dealer");
        }

        return singleDealers[0]?.id;
    }, [dealers]);

    return useMemo(() => ({ dealerId, toggle, dealerOptions }), [dealerOptions, dealerId, toggle]);
};

// Use multiple dealer ids
// Should be use in admin portal
export const useMultipleDealerIds = () => {
    const {
        dealers,
        setDealers,
        dealerOptions,
        dealersFromApi,
        setDisplayDropdown,
        displayDropdown = false,
        setDealerOptions,
        dealerIdsByCompanyId,
    } = useDealerContext();

    // Toggle activation for one dealer id
    const toggle = useCallback(
        (newDealerId: string) => {
            const newDealerState = dealers?.map(dealer => {
                if (dealer.id === newDealerId) {
                    return { ...dealer, isMultiple: true };
                }

                return { ...dealer, isMultiple: false };
            });

            setDealers(newDealerState);
        },
        [dealers, setDealers]
    );

    // Toggle activation for all multiple dealers
    const toggleAll = useCallback(() => {
        const newDealersState = dealers.map(dealer => ({ ...dealer, isMultiple: true }));

        setDealers(newDealersState);
    }, [dealers, setDealers]);

    const dealerIds = useMemo(() => dealers?.filter(dealer => dealer.isMultiple).map(dealer => dealer.id), [dealers]);

    const setAllVisible = useCallback(() => {
        const newDealer = dealers.map(dealer => ({ ...dealer, visible: true }));
        setDealers(newDealer);
    }, [dealers, setDealers]);

    const setVisible = useCallback(
        (dealerIds: string[], visible: boolean) => {
            const existedDealer = dealers.map(dealer => {
                if (dealerIds.includes(dealer.id)) {
                    return { ...dealer, visible };
                }

                return dealer;
            });
            setDealers(existedDealer);
        },
        [dealers, setDealers]
    );

    return useMemo(
        () => ({
            dealerIds,
            toggle,
            toggleAll,
            dealerOptions,
            dealersFromApi,
            setDisplayDropdown,
            displayDropdown,
            setAllVisible,
            setVisible,
            setDealerOptions,
            dealers,
            dealerIdsByCompanyId,
        }),
        [
            dealerIds,
            toggle,
            toggleAll,
            dealerOptions,
            dealersFromApi,
            setDisplayDropdown,
            displayDropdown,
            setAllVisible,
            setVisible,
            setDealerOptions,
            dealers,
            dealerIdsByCompanyId,
        ]
    );
};
